import { FC, useState, useEffect, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { AlertColor, CircularProgress, Divider, useTheme } from '@mui/material';
import Notification from '../../../../../../components/Notification';
import { AddEditPricePackage } from './AddEditPricePackage/AddEditPricePackage';
import { AddEditPriceOverRide } from './AddEditPriceOverRide/AddEditPriceOverRide';
import PrimaryButton from '../../../../../../components/Button/PrimaryButton';
import axiosInstance from '../../../../../../utils/axios';
import { URLS } from '../../../../../../utils/constants/urls';
import strings from '../../../../../../common/Translation/Translate';
import {
  buildPriceOverridePayload,
  mapPriceOverridesFromApi,
  mapPricePackagesFromApi,
  PriceOverrideRow,
  PricePackageRow,
  validatePriceOverrideRow,
  validatePricePackageRow,
} from './priceFormUtils';

type PriceProps = {
  dropdownData?: any;
  pageResponse: any;
  getPageData: (id: number) => void;
};

const Price: FC<PriceProps> = ({ dropdownData, pageResponse, getPageData }) => {
  const { id } = useParams() as any;
  const theme = useTheme();
  const queryStings = useLocation().search as any;
  const queryStingId = new URLSearchParams(queryStings).get('id') as string;
  const [pricePackage, setPricePackage] = useState<PricePackageRow[]>([]);
  const [priceOverride, setPriceOverride] = useState<PriceOverrideRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  useEffect(() => {
    if (id || queryStingId) {
      setPricePackage(mapPricePackagesFromApi(pageResponse.pricePackages ?? []));
      setPriceOverride(mapPriceOverridesFromApi(pageResponse.priceOverRides ?? []));
    }
  }, [pageResponse.pricePackages, pageResponse.priceOverRides, id, queryStingId]);

  const validateAll = useCallback((): string => {
    for (const row of pricePackage) {
      const error = validatePricePackageRow(row);
      if (error) return error;
    }
    for (const row of priceOverride) {
      const error = validatePriceOverrideRow(row);
      if (error) return error;
    }
    return '';
  }, [pricePackage, priceOverride]);

  const handleSubmit = useCallback(async () => {
    const validationError = validateAll();
    if (validationError) {
      setMessage({ display: true, severity: 'error', message: validationError });
      return;
    }

    try {
      const senData = {
        pricePackages:
          pricePackage?.map(item => ({
            ...item,
            pricePackageId: item.pricePackageId,
            pricePackageName: item.pricePackageName,
          })) || [],
        priceOverRides: priceOverride?.map((item, index) => buildPriceOverridePayload(item, index)) || [],
      };
      setLoading(true);
      const { status } = await axiosInstance.put(URLS.venueConfigsUpdatePrice(id || queryStingId), senData);
      if (status === 200) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.common_update_message,
        });
        getPageData(Number(id || queryStingId));
      }
    } catch (e: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: e.response?.data?.message ?? e.message,
      });
    } finally {
      setLoading(false);
    }
  }, [validateAll, pricePackage, priceOverride, id, queryStingId, getPageData]);

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <AddEditPricePackage editedData={pricePackage} setData={setPricePackage} dropdownValue={dropdownData} />
      <Divider sx={{ my: 3 }} />
      <AddEditPriceOverRide editedData={priceOverride} setData={setPriceOverride} dropdownValue={dropdownData} />
      <Divider sx={{ my: 2 }} />
      <PrimaryButton
        disabled={loading}
        sx={{
          minWidth: '100px',
          '&.defaultButton': { background: theme.palette.primary.main, color: theme.palette.primary.contrastText },
        }}
        fullWidth={false}
        variant="contained"
        onClick={handleSubmit}>
        {loading && <CircularProgress color="inherit" sx={{ ml: 1 }} size={20} />}
        {strings.common_button_save}
      </PrimaryButton>
    </>
  );
};
export default Price;
